import type { Reading } from '@casa/driver-sdk';
import type { Tile } from '@casa/model';
import { byValuesOf, sourceNamesOf } from '../tiles/thermostat/thermostat_tile.ts';
import type { TileEnvironment } from './tile_environment.ts';

// Sample data for previews of a tile that has no data yet (tile-styles.md TY-5): a tile picking a
// style before its source is chosen. A source the tile has shows its live readings; one it does
// not have shows a sample reading for the tile's type. Types without a sample show no data, as
// they would live.

const HOUR_MS = 3_600_000;

/** A day of hourly readings, a smooth curve from `low` to `high` and back, bent to end at `last` without a jump. */
function day(low: number, high: number, last: number, phase: number) : Reading[] {
    const end = Math.floor(Date.now() / HOUR_MS) * HOUR_MS;
    const curve = (hour: number) : number => low + ((high - low) * (1 - Math.cos(((hour + phase) / 24) * 2 * Math.PI))) / 2;
    const bend = last - curve(24);
    const readings: Reading[] = [];
    for (let hour = 0; hour <= 24; hour++) {
        readings.push({ time: end - (24 - hour) * HOUR_MS, value: Math.round((curve(hour) + (bend * hour) / 24) * 10) / 10 });
    }

    return readings;
}

/** The sample readings of the source `name` of `tile`; undefined where its type has none. */
function sampleOf(tile: Tile, name: string) : Reading[] | undefined {
    switch (tile.type) {
        case 'value': {
            const { gauge } = tile.options;
            if (name === gauge?.secondary?.source) {
                return [{ value: 21 }];
            }
            const more = gauge?.more.findIndex((each) => each.source === name) ?? -1;
            if (more >= 0) {
                return [{ value: 14 + 3 * more }];
            }

            return day(18.5, 23, 21.5, 0);
        }
        case 'graph': {
            const index = Math.max(0, tile.options.graph.series.findIndex((series) => series.source === name));

            return day(16 + 3 * index, 23 + 2 * index, 21.5 + 2 * index, 4 * index);
        }
        case 'thermostat': {
            if (!byValuesOf(tile.options)) {
                return [{ value: { temperature: 20.5, setpoint: 21, mode: 'heat', state: 'heating' } }];
            }
            const [temperature, setpoint] = sourceNamesOf(tile.options);

            return [{ value: name === temperature ? 20.5 : name === setpoint ? 21 : 'heat' }];
        }
        case 'light':
            return [{ value: { switch: 'on', level: 60 } }];
        case 'switch':
            return [{ value: true }];
        case 'status':
            return [{ value: 'on' }];
        default:
            return undefined;
    }
}

/** Whether `tile` shows sample data for the source `name`: it reads one it does not have. */
const isSampled = (tile: Tile, name: string | undefined) : name is string => name !== undefined && tile.dataSources[name] === undefined;

/**
 * `tile` as a style's preview shows it (TY-5): titled `title` where it has none, and without its
 * style rules. A value tile showing sample data shows it in °C where it has no unit of its own,
 * with a sample caption where it has none and `withCaption` (a style that styles the caption),
 * and on a gauge from 10 to 30 where its gauge has the range a style gives a new one.
 */
export function previewTileOf<T extends Tile>(tile: T, title: string, withCaption = false) : T {
    const titled = { ...tile, label: tile.label ?? title };
    if (titled.type !== 'value') {
        return titled;
    }

    const { options } = titled;
    const isSample = isSampled(titled, options.source);
    const isNewGauge = options.gauge !== undefined && options.gauge.min === 0 && options.gauge.max === 100;

    return {
        ...titled,
        options: {
            ...options,
            styleRules: [],
            unit: options.unit === '' && isSample ? '°C' : options.unit,
            ...(options.caption === undefined && isSample && withCaption ? { caption: 'Comfortable' } : {}),
            ...(options.gauge && isSample && isNewGauge ? { gauge: { ...options.gauge, min: 10, max: 30 } } : {}),
        },
    } as T;
}

/**
 * `live` with sample readings for the sources a tile reads but does not have, and its status as
 * fine while it has none at all. Its hooks call `live`'s always, in the same order.
 */
export function sampleEnvironment(live: TileEnvironment) : TileEnvironment {
    const samples = new Map<string, Reading[] | undefined>();
    // One list per tile type and source, so that views that draw again on new readings do not.
    const sampleFor = (tile: Tile, name: string) : Reading[] | undefined => {
        const key = `${tile.type}/${name}`;
        if (!samples.has(key)) {
            samples.set(key, sampleOf(tile, name));
        }

        return samples.get(key);
    };

    return {
        ...live,
        useReadings: (tile, name) => {
            const readings = live.useReadings(tile, name);

            return isSampled(tile, name) ? sampleFor(tile, name) : readings;
        },
        useReadingsList: (tile, names) => {
            const readings = live.useReadingsList(tile, names);

            return names.map((name, index) => (isSampled(tile, name) ? sampleFor(tile, name) : readings[index]));
        },
        useStatus: (tile) => {
            const status = live.useStatus(tile);

            return Object.keys(tile.dataSources).length === 0 ? { status: 'ok', failures: [] } : status;
        },
    };
}
